import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const SECTIONS = [
  { id: 'overview',    label: '서비스 소개' },
  { id: 'start',       label: '시작하기' },
  { id: 'upload',      label: '데이터 등록' },
  { id: 'merge',       label: '타일셋 병합' },
  { id: 'formats',     label: '지원 파일 형식' },
  { id: 'crs',         label: '좌표 참조계(CRS)' },
  { id: 'viewer',      label: '3D 뷰어' },
  { id: 'style',       label: '포인트 클라우드 스타일' },
  { id: 'measure',     label: '측정 도구' },
  { id: 'manage',      label: '데이터 관리' },
  { id: 'account',     label: '계정 관리' },
]

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-xl font-bold text-slate-800 border-b border-slate-200 pb-2 mb-5">{title}</h2>
      <div className="space-y-4 text-slate-700 leading-relaxed">{children}</div>
    </section>
  )
}

function Step({ num, children }: { num: number; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">{num}</span>
      <span>{children}</span>
    </div>
  )
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block bg-slate-100 text-slate-700 text-xs font-mono px-2 py-0.5 rounded border border-slate-200 mr-1 mb-1">{children}</span>
  )
}

function Note({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
      <span className="shrink-0 mt-0.5">ℹ️</span>
      <span>{children}</span>
    </div>
  )
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
      <span className="shrink-0 mt-0.5">⚠️</span>
      <span>{children}</span>
    </div>
  )
}

export default function ManualPage() {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('overview')

  const handleNavClick = (id: string) => {
    setActiveSection(id)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

      {/* 헤더 */}
      <header className="h-12 bg-slate-900 flex items-center justify-between px-4 shrink-0 z-10">
        <div className="flex items-center gap-3">
          <img src="/gaia3d-logo.png" alt="Gaia3D" className="h-6 object-contain" />
          <span className="text-white text-sm font-semibold">Mago 3D Reality Mesh — 사용자 매뉴얼</span>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="text-xs text-slate-400 hover:text-white border border-slate-600 hover:border-slate-400 rounded px-3 py-1 transition-colors flex items-center gap-1.5"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          대시보드로 돌아가기
        </button>
      </header>

      {/* 본문 */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* 사이드 네비게이션 */}
        <nav className="w-52 bg-slate-50 border-r border-slate-200 shrink-0 overflow-y-auto py-6">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest px-5 mb-3">목차</p>
          <ul>
            {SECTIONS.map(s => (
              <li key={s.id}>
                <button
                  onClick={() => handleNavClick(s.id)}
                  className={`w-full text-left px-5 py-2 text-sm transition-colors ${
                    activeSection === s.id
                      ? 'bg-blue-50 text-blue-700 font-semibold border-r-2 border-blue-600'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* 콘텐츠 */}
        <main
          className="flex-1 overflow-y-auto px-10 py-8"
          onScroll={(e) => {
            const el = e.currentTarget
            for (const s of SECTIONS) {
              const sec = document.getElementById(s.id)
              if (sec && sec.offsetTop - el.scrollTop <= 120) {
                setActiveSection(s.id)
              }
            }
          }}
        >
          <div className="max-w-3xl mx-auto">

            {/* ── 1. 서비스 소개 ── */}
            <Section id="overview" title="1. 서비스 소개">
              <p>
                <strong>Mago 3D Reality Mesh Service</strong>는 도시 Mesh 데이터를 <strong>3D Tiles</strong> 형식으로
                변환하고, CesiumJS 기반 웹 뷰어에서 3차원으로 시각화하는 서비스입니다.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2">
                {[
                  { icon: '📤', title: '데이터 등록', desc: '다양한 3D/공간 파일을 업로드하면 자동으로 3D Tiles로 변환합니다.' },
                  { icon: '🧩', title: '타일셋 병합', desc: '여러 데이터를 하나의 타일셋으로 병합해 함께 봅니다.' },
                  { icon: '🌍', title: '3D 시각화', desc: 'CesiumJS 기반 지구 뷰어에서 변환된 타일을 즉시 확인합니다.' },
                  { icon: '📐', title: '공간 측정', desc: '거리·면적·부피를 3D 공간에서 직접 측정합니다.' },
                ].map(c => (
                  <div key={c.title} className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                    <div className="text-3xl mb-2">{c.icon}</div>
                    <div className="font-semibold text-slate-800 text-sm mb-1">{c.title}</div>
                    <div className="text-xs text-slate-500">{c.desc}</div>
                  </div>
                ))}
              </div>
            </Section>

            {/* ── 2. 시작하기 ── */}
            <Section id="start" title="2. 시작하기">
              <h3 className="font-semibold text-slate-800">회원가입</h3>
              <div className="space-y-2">
                <Step num={1}>로그인 화면에서 <strong>회원가입</strong> 링크를 클릭합니다.</Step>
                <Step num={2}><strong>아이디</strong>를 입력한 후 <strong>중복 확인</strong> 버튼을 눌러 사용 가능 여부를 확인합니다.</Step>
                <Step num={3}>이메일, 이름, 비밀번호(8자 이상)를 입력하고 <strong>회원가입</strong>을 완료합니다.</Step>
              </div>
              <Note>아이디 중복 확인을 완료해야 가입이 가능합니다.</Note>

              <h3 className="font-semibold text-slate-800 mt-6">로그인</h3>
              <div className="space-y-2">
                <Step num={1}>아이디와 비밀번호를 입력 후 <strong>로그인</strong>을 클릭합니다.</Step>
                <Step num={2}><strong>네이버 아이디로 로그인</strong> 버튼을 통해 소셜 로그인도 지원합니다.</Step>
              </div>
              <Warn>로그인 세션은 <strong>1시간</strong> 후 자동으로 만료됩니다. 작업 중 세션이 만료되면 자동으로 로그아웃됩니다.</Warn>
            </Section>

            {/* ── 3. 데이터 등록 ── */}
            <Section id="upload" title="3. 데이터 등록">
              <p>사이드바 상단의 <strong>+ 등록</strong> 버튼을 클릭하면 아래 4가지 방식 중 하나를 선택할 수 있습니다.</p>

              <div className="grid grid-cols-2 gap-3 mt-2">
                {[
                  ['간편 등록', '파일과 기본 정보만으로 빠르게 등록합니다. 좌표계·출력 옵션은 자동으로 추천됩니다.'],
                  ['상세 옵션 등록', 'mago-3d-tiler의 모든 변환 옵션(LOD, Proj4, 오프셋 등)을 4단계로 나누어 직접 설정합니다.'],
                  ['기존 타일셑 병합', '이미 변환 완료된 데이터 여러 개를 재변환 없이 하나의 타일셋으로 병합합니다.'],
                  ['신규 타일셑 병합', '아직 변환하지 않은 원본 파일 여러 개를 순차 변환한 뒤 하나로 병합합니다.'],
                ].map(([t, d]) => (
                  <div key={t} className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-sm font-semibold text-slate-800">{t}</p>
                    <p className="text-xs text-slate-500 mt-1">{d}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400">타일셋 병합 두 가지 방식의 자세한 사용법은 아래 <strong>"4. 타일셋 병합"</strong> 항목을 참고하세요.</p>

              <h3 className="font-semibold text-slate-800 mt-6">간편 등록</h3>
              <div className="space-y-2">
                <Step num={1}>
                  파일을 드래그&드롭하거나 클릭하여 선택합니다. <strong>폴더 업로드</strong>로 전환하면 폴더 전체를
                  자동으로 압축해 하나의 데이터로 등록할 수 있습니다(예: OBJ + MTL + 텍스처처럼 여러 파일로
                  구성된 데이터셋).
                </Step>
                <Step num={2}>데이터 이름과 설명을 입력합니다.</Step>
                <Step num={3}>좌표 참조계(CRS)를 선택합니다. 일반적인 국내 데이터는 <strong>EPSG:5186</strong>을 사용합니다.</Step>
                <Step num={4}><strong>등록</strong> 버튼을 클릭하면 업로드 및 변환이 자동으로 시작됩니다.</Step>
              </div>

              <h3 className="font-semibold text-slate-800 mt-6">상세 옵션 등록</h3>
              <p className="text-sm">
                입력 형식, 출력 형식, 3D Tiles 버전 등 세부 변환 옵션을 직접 지정할 수 있으며, 마지막 단계에서
                실제 실행될 mago-3d-tiler CLI 명령을 미리보기로 확인할 수 있습니다. 간편 등록과 마찬가지로
                폴더 업로드를 지원합니다.
              </p>
              <div className="overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">옵션</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">설명</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ['입력 형식', '파일 형식을 수동으로 지정합니다. 자동 감지 선택 시 확장자로 추론합니다.'],
                      ['출력 형식', 'B3DM(일반 3D), I3DM(인스턴스), PNTS(포인트 클라우드) 중 선택합니다.'],
                      ['3D Tiles 버전', '1.0 또는 1.1을 선택합니다. 최신 뷰어와 호환하려면 1.1을 권장합니다.'],
                      ['좌표 참조계', '데이터의 원본 좌표계를 지정합니다.'],
                      ['LOD 설정', '최소·최대 상세 수준(Level of Detail) 깊이를 지정합니다.'],
                      ['좌표 오프셋 / 원점', 'X·Y·Z 오프셋 또는 로컬 데이터의 지리 좌표 원점(경도·위도)을 직접 지정합니다.'],
                    ].map(([opt, desc]) => (
                      <tr key={opt}>
                        <td className="px-4 py-2 font-medium text-slate-800 whitespace-nowrap">{opt}</td>
                        <td className="px-4 py-2 text-slate-600">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Note>변환이 완료되면 등록 시 입력한 이메일로 알림이 발송됩니다.</Note>
              <Note>변환 중에는 상태가 <strong>변환중</strong>으로 표시되며, 완료 후 <strong>완료</strong>로 변경됩니다.</Note>
            </Section>

            {/* ── 4. 타일셋 병합 ── */}
            <Section id="merge" title="4. 타일셋 병합">
              <p>
                서로 다른 위치나 시기에 변환한 여러 데이터를 하나의 타일셋으로 합쳐서 보고 싶을 때 사용합니다.
                병합 결과는 원본과 별개의 새 데이터로 목록에 추가되며, 원본 데이터는 그대로 유지됩니다.
              </p>

              <h3 className="font-semibold text-slate-800 mt-4">기존 타일셑 병합</h3>
              <p className="text-sm">이미 <strong>완료</strong> 상태인 데이터 여러 개를 골라 재변환 없이 즉시 하나의 타일셋으로 묶습니다.</p>
              <div className="space-y-2">
                <Step num={1}><strong>+ 등록</strong> → <strong>기존 타일셑 병합</strong>을 선택합니다.</Step>
                <Step num={2}>병합 결과 이름을 입력하고, 변환 완료된 데이터를 체크박스로 2개 이상 선택합니다.</Step>
                <Step num={3}><strong>병합</strong> 버튼을 클릭하면 재변환 과정 없이 즉시 완료됩니다.</Step>
              </div>
              <Note>
                원본 데이터가 나중에 삭제되면 병합 결과에서 해당 부분도 함께 사라집니다. 반대로 원본에 높이
                보정 등을 적용하면 병합 결과에도 자동으로 반영됩니다(원본을 실시간으로 참조하는 방식이기 때문).
              </Note>

              <h3 className="font-semibold text-slate-800 mt-6">신규 타일셑 병합</h3>
              <p className="text-sm">
                아직 변환하지 않은 원본 파일 여러 개를 올려 각각 순차적으로 3D Tiles 변환한 뒤, mago-3d-tiler의
                자체 병합 기능으로 하나의 타일셋으로 합칩니다.
              </p>
              <div className="space-y-2">
                <Step num={1}><strong>+ 등록</strong> → <strong>신규 타일셑 병합</strong>을 선택합니다.</Step>
                <Step num={2}>
                  병합 결과 이름을 입력하고, <strong>+ 파일 추가</strong> 또는 <strong>+ 폴더 추가</strong>로
                  원본을 2개 이상 담습니다. 폴더를 선택하면 상세 옵션 등록의 폴더 업로드와 마찬가지로 폴더
                  전체가 압축되어 하나의 원본으로 추가됩니다.
                </Step>
                <Step num={3}>
                  필요하면 각 원본 항목의 <strong>좌표 설정</strong>을 펼쳐 원본별로 좌표계·오프셋·경위도 원점을
                  다르게 지정할 수 있습니다(서로 다른 출처의 데이터를 섞어 병합할 때 유용합니다).
                </Step>
                <Step num={4}>출력 포맷·3D Tiles 버전·LOD 등 병합 결과 전체에 적용될 공통 옵션을 지정한 뒤 <strong>변환 및 병합 시작</strong>을 클릭합니다.</Step>
              </div>
              <Note>원본 업로드가 끝나면 서버에서 순차 변환·병합이 진행되며, 완료 시 이메일로 알려드립니다. 변환이 시작되기 전까지는 데이터 목록에 표시되지 않습니다.</Note>
              <Warn>원본 중 하나라도 변환에 실패하면 병합 전체가 실패로 처리됩니다(부분 병합 결과는 저장되지 않습니다).</Warn>
            </Section>

            {/* ── 5. 지원 파일 형식 ── */}
            <Section id="formats" title="5. 지원 파일 형식">
              <div className="overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">확장자</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">설명</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ['.obj',     'Wavefront OBJ — 3D 메시'],
                      ['.ply',     'Stanford PLY — 포인트 클라우드 / 메시'],
                      ['.fbx',     'Autodesk FBX — 3D 애니메이션/메시'],
                      ['.las / .laz', 'LiDAR 포인트 클라우드 (압축)'],
                      ['.gltf / .glb', 'GL Transmission Format — 3D 씬'],
                      ['.dae',     'COLLADA DAE — 3D 자산'],
                      ['.ifc',     'Industry Foundation Classes — BIM 데이터'],
                      ['.gml',     'CityGML — 도시 3D 모델'],
                      ['.shp',     'ESRI Shapefile — 벡터 공간 데이터'],
                      ['.geojson', 'GeoJSON — 벡터 공간 데이터'],
                      ['.gpkg',    'GeoPackage — 공간 데이터베이스'],
                      ['.kml',     'Keyhole Markup Language'],
                      ['.zip',     '위 형식을 압축한 ZIP 아카이브'],
                    ].map(([ext, desc]) => (
                      <tr key={ext}>
                        <td className="px-4 py-2 font-mono text-blue-700 whitespace-nowrap">{ext}</td>
                        <td className="px-4 py-2 text-slate-600">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Note>여러 파일로 구성된 데이터(예: .obj + .mtl + 텍스처, .shp + .dbf + .prj)는 폴더 업로드 기능을 이용하면 자동으로 압축되어 올라갑니다. 직접 ZIP으로 압축해서 올려도 됩니다.</Note>
            </Section>

            {/* ── 6. 좌표 참조계 ── */}
            <Section id="crs" title="6. 좌표 참조계 (CRS)">
              <p>업로드하는 데이터의 원본 좌표계를 정확히 지정해야 3D 지구 위에 올바른 위치에 표시됩니다.</p>
              <div className="overflow-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">EPSG 코드</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">명칭</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">주요 용도</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ['5186', 'Korea 2000 Central (TM 중부원점)', '국내 일반 측량 데이터 (기본 권장)'],
                      ['5179', 'Korea 2000 / UTM-K (GRS80)',       '국토지리정보원 수치지형도'],
                      ['5174', 'Korean 1985 Central (Bessel 중부)', '구형 국내 측량 데이터'],
                      ['5187', 'Korea 2000 East (TM 동부원점)',     '동부 지역 측량 데이터'],
                      ['32652', 'UTM Zone 52N',                     '동아시아 국제 좌표계'],
                      ['3857', 'Web Mercator',                      '구글 맵, 오픈스트리트맵'],
                      ['4326', 'WGS 84 (위경도)',                   '위도/경도 기반 GPS 데이터'],
                      ['4978', 'WGS84 ECEF',                        '지구중심 3D 좌표 (항공·위성)'],
                    ].map(([code, name, usage]) => (
                      <tr key={code}>
                        <td className="px-4 py-2 font-mono text-blue-700 whitespace-nowrap">EPSG:{code}</td>
                        <td className="px-4 py-2 text-slate-700">{name}</td>
                        <td className="px-4 py-2 text-slate-500 text-xs">{usage}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Warn>좌표계를 잘못 선택하면 3D 지구에서 엉뚱한 위치에 데이터가 표시됩니다.</Warn>
            </Section>

            {/* ── 7. 3D 뷰어 ── */}
            <Section id="viewer" title="7. 3D 뷰어">
              <h3 className="font-semibold text-slate-800">지도 탐색</h3>
              <div className="overflow-auto rounded-lg border border-slate-200 mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">조작</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">동작</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ['마우스 왼쪽 버튼 드래그', '지도 회전'],
                      ['마우스 오른쪽 버튼 드래그', '지도 이동 (패닝)'],
                      ['마우스 휠 스크롤', '줌 인 / 줌 아웃'],
                      ['더블 클릭', '해당 지점으로 카메라 이동'],
                      ['Ctrl + 드래그', '카메라 기울기 조절'],
                    ].map(([action, desc]) => (
                      <tr key={action}>
                        <td className="px-4 py-2 font-medium text-slate-800">{action}</td>
                        <td className="px-4 py-2 text-slate-600">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="font-semibold text-slate-800 mt-6">타일셋 On/Off</h3>
              <div className="space-y-2">
                <Step num={1}>왼쪽 데이터 목록에서 <strong>완료</strong> 상태인 항목을 클릭합니다.</Step>
                <Step num={2}>3D 타일이 지도에 표시되며, 클릭한 타일로 카메라가 자동 이동합니다.</Step>
                <Step num={3}>동일 항목을 다시 클릭하면 타일이 숨겨집니다.</Step>
              </div>

              <h3 className="font-semibold text-slate-800 mt-6">배경지도 변경</h3>
              <p className="text-sm">지도 우상단의 <strong>배경지도</strong> 버튼을 클릭하여 V-World 배경지도 종류를 변경할 수 있습니다.</p>
              <div className="flex flex-wrap gap-2 mt-2">
                {['기본(Base)', '회색(Gray)', '야간(Night)', '위성(Satellite)'].map(t => (
                  <Tag key={t}>{t}</Tag>
                ))}
              </div>
            </Section>

            {/* ── 8. 포인트 클라우드 스타일 ── */}
            <Section id="style" title="8. 포인트 클라우드 스타일">
              <p>
                LAS/LAZ로 변환된 포인트 클라우드 데이터는 데이터 목록에서 항목을 뷰어에 표시한 뒤, 항목 하단에
                나타나는 컨트롤로 색상 모드·점 크기·원근 감쇠를 직접 조절할 수 있습니다.
              </p>

              <h3 className="font-semibold text-slate-800 mt-4">색상 모드</h3>
              <div className="overflow-auto rounded-lg border border-slate-200 mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">모드</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">설명</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ['원본 색상 (RGB)', '라이다 스캔에 기록된 실제 색상을 그대로 표시합니다.'],
                      ['분류별 색상 (Classification)', '지면·건물·식생·수면 등 ASPRS 분류 코드별로 색상을 구분합니다.'],
                      ['반사강도 흑백 (Intensity)', '레이저 반사강도를 흑백 명암으로 표시합니다.'],
                      ['반사강도 히트맵 (Intensity)', '반사강도를 구간별 색상(파랑 → 초록 → 노랑 → 빨강)으로 표시합니다.'],
                      ['높이별 색상 (Elevation)', '고도값에 따라 그라디언트 색상으로 표시합니다.'],
                    ].map(([mode, desc]) => (
                      <tr key={mode}>
                        <td className="px-4 py-2 font-medium text-slate-800 whitespace-nowrap">{mode}</td>
                        <td className="px-4 py-2 text-slate-600">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Note>분류·반사강도 히트맵·높이 모드를 사용 중이면 화면 좌하단 측정 도구 패널 옆에 범례(색상별 의미)가 함께 표시됩니다.</Note>

              <h3 className="font-semibold text-slate-800 mt-6">점 크기 · 원근 감쇠</h3>
              <p className="text-sm">
                점 크기(1~5px)를 슬라이더로 조절할 수 있고, <strong>원근 감쇠</strong>를 켜면 카메라와의 거리에
                따라 점이 자연스럽게 작아져 더 부드럽게 렌더링됩니다.
              </p>

              <Warn>포인트 클라우드는 구조상 투명도 조절을 지원하지 않습니다(항상 불투명). 메시(B3DM/I3DM) 데이터는 투명도 슬라이더를 사용할 수 있습니다.</Warn>
            </Section>

            {/* ── 9. 측정 도구 ── */}
            <Section id="measure" title="9. 측정 도구">
              <p>지도 좌하단의 <strong>측정 도구</strong> 패널에서 공간 측정 기능을 사용할 수 있습니다. 3D 타일이 하나 이상 활성화된 경우에 사용하세요.</p>

              {[
                {
                  title: '거리 측정',
                  steps: [
                    '측정 도구 패널에서 거리 측정을 클릭합니다.',
                    '지도에서 측정할 시작 지점을 클릭합니다.',
                    '경유 지점을 순서대로 클릭합니다.',
                    '완료 버튼을 누르면 총 거리가 표시됩니다.',
                  ],
                },
                {
                  title: '면적 측정',
                  steps: [
                    '측정 도구 패널에서 면적 측정을 클릭합니다.',
                    '지도에서 꼭짓점을 순서대로 클릭하여 폴리곤을 그립니다.',
                    '완료 버튼을 누르면 면적이 표시됩니다.',
                  ],
                },
                {
                  title: '부피 측정',
                  steps: [
                    '측정 도구 패널에서 부피 측정을 클릭합니다.',
                    '측정할 영역의 꼭짓점을 클릭합니다.',
                    '완료 버튼을 누른 후 기준 높이를 입력합니다.',
                    '3D 메시를 기준으로 부피를 계산합니다.',
                  ],
                },
              ].map(({ title, steps }) => (
                <div key={title} className="mt-4">
                  <h3 className="font-semibold text-slate-800 mb-2">{title}</h3>
                  <div className="space-y-1.5">
                    {steps.map((s, i) => <Step key={i} num={i + 1}>{s}</Step>)}
                  </div>
                </div>
              ))}

              <Note>측정 결과를 초기화하려면 <strong>초기화</strong> 버튼을 클릭합니다.</Note>
            </Section>

            {/* ── 10. 데이터 관리 ── */}
            <Section id="manage" title="10. 데이터 관리">
              <h3 className="font-semibold text-slate-800">데이터 목록</h3>
              <p className="text-sm">대시보드 오른쪽 사이드바에 내 데이터 목록이 표시됩니다. 상단 검색창으로 이름 필터링이 가능합니다.</p>
              <div className="overflow-auto rounded-lg border border-slate-200 mt-2">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">상태</th>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">의미</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {[
                      ['대기중',  '변환 대기 중입니다.'],
                      ['변환중',  '3D Tiles로 변환 작업 중입니다.'],
                      ['완료',    '변환이 완료되어 3D 뷰어에서 확인 가능합니다.'],
                      ['실패',    '변환에 실패했습니다. 파일과 좌표계를 확인 후 다시 등록하세요.'],
                    ].map(([status, desc]) => (
                      <tr key={status}>
                        <td className="px-4 py-2 font-medium text-slate-800">{status}</td>
                        <td className="px-4 py-2 text-slate-600">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="font-semibold text-slate-800 mt-6">URL 복사</h3>
              <p className="text-sm">
                데이터 목록의 <strong>URL 복사</strong> 버튼을 클릭하면 접근 토큰이 포함된 Tile URL이
                클립보드에 복사됩니다. 외부 Cesium 뷰어나 다른 도구에서 변환된 타일을 직접 불러올 때
                사용할 수 있습니다.
              </p>

              <h3 className="font-semibold text-slate-800 mt-6">다운로드</h3>
              <p className="text-sm">데이터 목록의 <strong>다운로드</strong> 버튼을 클릭하면 변환된 3D Tiles 전체를 ZIP 파일로 내려받습니다.</p>

              <h3 className="font-semibold text-slate-800 mt-6">높이(고도) 보정</h3>
              <p className="text-sm">
                변환이 완료된 데이터의 높이가 지형과 맞지 않을 때, 처음부터 다시 변환할 필요 없이
                <strong> 높이 보정</strong> 버튼으로 바로 위·아래로 이동시킬 수 있습니다.
              </p>
              <div className="space-y-2">
                <Step num={1}>데이터 목록에서 <strong>높이 보정</strong> 버튼을 클릭합니다.</Step>
                <Step num={2}>보정할 값을 미터 단위로 입력합니다(양수 = 위로, 음수 = 아래로).</Step>
                <Step num={3}><strong>적용</strong>을 클릭하면 즉시 반영됩니다.</Step>
              </div>
              <Warn>이 보정은 저장된 타일 데이터 자체를 직접 수정해 영구적으로 적용됩니다. 되돌리려면 반대 부호로 다시 적용하세요.</Warn>

              <h3 className="font-semibold text-slate-800 mt-6">투명도</h3>
              <p className="text-sm">메시(B3DM/I3DM) 데이터는 뷰어에 표시된 상태에서 항목 하단의 투명도 슬라이더로 불투명도를 조절할 수 있습니다. 포인트 클라우드(LAS)는 지원하지 않습니다.</p>

              <h3 className="font-semibold text-slate-800 mt-6">삭제</h3>
              <p className="text-sm">데이터 목록의 <strong>삭제</strong> 버튼을 클릭하면 해당 데이터와 변환 파일이 영구적으로 삭제됩니다.</p>
              <Warn>삭제된 데이터는 복구할 수 없습니다. 이 데이터가 "기존 타일셑 병합" 결과에 포함되어 있다면, 병합 결과에서도 함께 사라집니다.</Warn>
            </Section>

            {/* ── 11. 계정 관리 ── */}
            <Section id="account" title="11. 계정 관리">
              <h3 className="font-semibold text-slate-800">내 정보 확인</h3>
              <p className="text-sm">
                헤더 우측의 <strong>내 정보</strong> 버튼을 클릭하면 아이디, 이름, 이메일, 소속, 가입일 등
                계정 정보와 함께 현재 요금제·쿼터(변환·저장·스트리밍 용량, 다운로드 가능 여부)를 확인할 수
                있습니다.
              </p>

              <h3 className="font-semibold text-slate-800 mt-6">로그아웃</h3>
              <p className="text-sm">헤더 우측의 <strong>로그아웃</strong> 버튼을 클릭하면 즉시 로그아웃됩니다.</p>

              <h3 className="font-semibold text-slate-800 mt-6">자동 로그아웃</h3>
              <p className="text-sm">보안을 위해 로그인 후 <strong>1시간</strong>이 경과하면 자동으로 로그아웃됩니다. 이후 다시 로그인하면 서비스를 계속 이용할 수 있습니다.</p>
            </Section>

            <div className="border-t border-slate-200 pt-6 pb-10 text-center text-xs text-slate-400">
              © Gaia3D, Inc. — Mago 3D Reality Mesh Service
            </div>

          </div>
        </main>
      </div>
    </div>
  )
}
